import React, { useState, useMemo } from "react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import { format, parse } from "date-fns";

const parseDate = (s) => parse(s, "yyyy-MM-dd HH:mm:ss:SSS", new Date());
const group = (data, fmt) =>
  Object.entries(
    data.reduce((acc, { User_Table }) => {
      const raw = User_Table?.CREATEDTIME;
      if (!raw) return acc;
      const key = format(parseDate(raw), fmt);
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {})
  ).map(([date, shops]) => ({ date, shops }));

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: "rgba(11, 46, 51, 0.95)",
      backdropFilter: "blur(10px)",
      border: "1px solid rgba(184, 227, 233, 0.2)",
      borderRadius: "12px",
      padding: "10px 14px",
      boxShadow: "0 10px 30px rgba(0,0,0,0.3)",
      color: "#fff"
    }}>
      <p style={{ fontSize: 10, color: "#B8E3E9", marginBottom: 4, textTransform: "uppercase", letterSpacing: 1 }}>{label}</p>
      <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
        <span style={{ fontSize: 20, fontWeight: 800, color: "#fff" }}>{payload[0].value}</span>
        <span style={{ fontSize: 11, fontWeight: 500, color: "#B8E3E9", opacity: 0.7 }}>New Shops</span>
      </div>
      <div style={{ marginTop: 6, paddingTop: 6, borderTop: "1px solid rgba(255,255,255,0.1)", fontSize: 9, color: "rgba(255,255,255,0.5)" }}>
        Growth Trend: Positive
      </div>
    </div>
  );
};

const FILTERS = [
  { key: "Daily", fmt: "MMM dd" },
  { key: "Weekly", fmt: "'Week' w" },
  { key: "Monthly", fmt: "MMM" },
];

const LineChartComponent = ({ userData }) => {
  const [filter, setFilter] = useState("Daily");
  const [hoveredData, setHoveredData] = useState(null);

  const chartData = useMemo(() => {
    if (!Array.isArray(userData) || !userData.length) return [];
    return group(userData, FILTERS.find(f => f.key === filter).fmt);
  }, [userData, filter]);

  return (
    <>
      <style>{`
        .cc-card { 
          background: #fff; border-radius: 16px; border: 1px solid #CBCBCB; 
          padding: 16px; box-shadow: 0 4px 20px rgba(0,0,0,0.04);
          height: 100%; display: flex; flex-direction: column;
          overflow: hidden; position: relative;
        }
        .cc-card::before {
          content: ''; position: absolute; top: 0; left: 0; right: 0; height: 4px;
          background: linear-gradient(90deg, #0B2E33, #931B5B, #4F7C82);
          opacity: 0.8;
        }
        .cc-hdr { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; }
        .cc-title-wrap { display: flex; flex-direction: column; }
        .cc-title { font-size: 14px; font-weight: 800; color: #0B2E33; letter-spacing: -0.2px; }
        .cc-sub { font-size: 10px; color: #9ca3af; font-weight: 500; }
        
        .cc-pills { display: flex; gap: 4px; background: #F2F2F2; padding: 3px; border-radius: 10px; border: 1px solid #E5E5E5; }
        .cc-pill {
          padding: 5px 12px; border: none; border-radius: 8px;
          font-size: 10px; font-weight: 700; cursor: pointer;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); background: transparent; color: #6b7280;
          font-family: 'Inter', sans-serif;
        }
        .cc-pill.on { background: #0B2E33; color: #B8E3E9; box-shadow: 0 4px 12px rgba(11, 46, 51, 0.25); transform: translateY(-1px); }
        .cc-pill:hover:not(.on) { background: #CBCBCB; color: #1f2937; }
        
        .cc-wrap { flex: 1; min-height: 0; margin-top: 10px; position: relative; }
        .cc-empty { height: 100%; display: flex; flex-direction: column; align-items: center; justify-content: center; color: #CBCBCB; font-size: 12px; gap: 8px; }
        
        .recharts-cartesian-grid-horizontal line { stroke: #F2F2F2; }
        .recharts-cartesian-grid-vertical line { display: none; }
      `}</style>

      <div className="cc-card">
        <div className="cc-hdr">
          <div className="cc-title-wrap">
            <div className="cc-title">📈 Registration Analytics</div>
            <div className="cc-sub">Shop growth & acquisition metrics</div>
          </div>
          <div className="cc-pills">
            {FILTERS.map(f => (
              <button key={f.key} className={`cc-pill ${filter === f.key ? "on" : ""}`} onClick={() => setFilter(f.key)}>
                {f.key}
              </button>
            ))}
          </div>
        </div>

        <div className="cc-wrap">
          {!chartData.length ? (
            <div className="cc-empty">
              <div style={{ width: 48, height: 48, borderRadius: '50%', background: '#F2F2F2', display: 'flex', alignItems: 'center', justifyCenter: 'center', fontSize: 20 }}>📊</div>
              <span>Awaiting analytics data...</span>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart 
                data={chartData} 
                margin={{ top: 10, right: 10, left: -25, bottom: 0 }}
                onMouseMove={(e) => e.activePayload && setHoveredData(e.activePayload[0].payload)}
                onMouseLeave={() => setHoveredData(null)}
              >
                <defs>
                  <linearGradient id="colorPrimary" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#0B2E33" stopOpacity={0.3} />
                    <stop offset="60%" stopColor="#0B2E33" stopOpacity={0.05} />
                    <stop offset="95%" stopColor="#0B2E33" stopOpacity={0} />
                  </linearGradient>
                  <filter id="glow" x="-20%" y="-20%" width="140%" height="140%">
                    <feGaussianBlur stdDeviation="3" result="blur" />
                    <feComposite in="SourceGraphic" in2="blur" operator="over" />
                  </filter>
                </defs>
                
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 9, fill: "#9ca3af", fontWeight: 600 }} 
                  axisLine={false} 
                  tickLine={false}
                  dy={8}
                />
                <YAxis 
                  allowDecimals={false} 
                  tick={{ fontSize: 9, fill: "#9ca3af", fontWeight: 600 }} 
                  axisLine={false} 
                  tickLine={false}
                />
                <Tooltip 
                  content={<CustomTooltip />} 
                  cursor={{ stroke: '#0B2E33', strokeWidth: 1, strokeDasharray: '4 4' }}
                />
                
                {/* Shadow/Glow Area */}
                <Area 
                  type="monotone" 
                  dataKey="shops" 
                  stroke="none" 
                  fill="url(#colorPrimary)" 
                  animationDuration={1500}
                />
                
                {/* Main Stroke Area */}
                <Area 
                  type="monotone" 
                  dataKey="shops" 
                  stroke="#0B2E33" 
                  strokeWidth={3} 
                  fill="transparent" 
                  dot={{ r: 0 }}
                  activeDot={{ 
                    r: 6, 
                    fill: "#0B2E33", 
                    stroke: "#fff", 
                    strokeWidth: 3,
                    boxShadow: "0 0 10px rgba(11,46,51,0.5)"
                  }}
                  animationDuration={1000}
                />

                {/* Accent Area (for highlight) */}
                <Area 
                  type="monotone" 
                  dataKey="shops" 
                  stroke="#931B5B" 
                  strokeWidth={1} 
                  fill="none" 
                  strokeOpacity={0.2}
                  animationDuration={2000}
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </>
  );
};

export default LineChartComponent;
